import { flow, mapValues, values } from 'lodash/fp';
import _isEmpty from 'lodash/isEmpty';
import _sortBy from 'lodash/sortBy';
import _uniq from 'lodash/uniq';
import { searchcollectionSkusAfromnavparam, searchcollectionSkus, getproductsMetadata } from '../../api/graphQL';
import { transformtochoicebasedsku, transformtochoicebasedProducts } from '../../utils/transformSearchModelSkus';
import { queryChildSkusFromParent } from '../../utils/queryChildskusfromParent';
import { Analytics } from '../../analytics/sendAnalyticsEvent';
import { tryGetCabinetSellerCacheWithCollection } from '../../api/cabinet-seller';

export const mapData = (data) => flow(
  mapValues.convert({ cap: false })((value, key) => ({ ...value, id: key })),
  values,
)(data);

export const getDefaultChoice = (choices) => {
  const mappedChoices = mapData(choices);
  const defaultChoice = mappedChoices.find((choice) => choice.isDefault);
  return defaultChoice;
};

export const getSelectedCategoryChoiceMetadata = (choiceMetadata, choices, selectedCategory) => {
  const mappedChoiceMetadata = mapData(choiceMetadata);
  return mappedChoiceMetadata.filter((metaData) => (
    _isEmpty(selectedCategory) ? getDefaultChoice(choices)?.id : metaData.id === selectedCategory
  ));
};

export const sortSections = (sections) => _sortBy(sections, ['sequence', 'Name']);

export const getAdvanceFilters = (collection, selectedCategory) => {
  const filterChoiceIds = collection.options ? collection.options[1]?.choices : [];
  const mappedChoices = mapData(collection.choices);
  const filterNames = [];
  const filterChoices = [];
  mappedChoices.forEach((choice) => {
    if (filterChoiceIds?.includes(parseInt(choice?.id, 10))) {
      filterNames.push(choice.shortLabel);
      filterChoices.push(choice);
    }
  });

  const filters = [];
  const selectedCategoryId = _isEmpty(selectedCategory) ? getDefaultChoice(collection.choices).id : selectedCategory;

  if (collection.fetchedCachedData && collection.fetchedCachedData[selectedCategoryId]) {
    collection.fetchedCachedData[selectedCategoryId].cachedData.forEach((product) => {
      if (product.keyProductFeatures) {
        const matchedFeatures = product.keyProductFeatures
          .filter((feature) => filterChoices.some((choice) => choice.metaDataItems[0].value === feature.name))
          .map((feature) => {
            const filterChoice = filterChoices.find((choice) => choice.metaDataItems[0].value === feature.name);
            return filterChoice ? { name: filterChoice.shortLabel, value: feature.value } : null;
          })
          .filter(Boolean); // Remove any null values

        filters.push(...matchedFeatures); // Add all matched features to the filters array
      }

    });
  }
  return { filterNames, filters };
};

export const getCollectionSkusAfterDataProcessed = async (
  selectedChoice, choices, choiceMetadata, graphBaseUrl, metadataBaseUrl, skusPerBatch, storeId, zipCode) => {
  const selectedCategoryCollectionData = {};

  if (choices && choiceMetadata) {
    if (selectedChoice) {
      const data = choiceMetadata[selectedChoice.id];
      const searchParams = data.searchParams;
      const sections = data.sections;

      try {
        const searchData = await searchcollectionSkusAfromnavparam(graphBaseUrl,
          skusPerBatch, storeId, zipCode, searchParams);
        const transformedData = transformtochoicebasedsku(selectedChoice.id,
          searchData.choicebasedProducts, searchData.choicebasedDimensions);
        selectedCategoryCollectionData.searchModelSKus = transformedData;

        const superSkuProducts = searchData
          .choicebasedProducts.filter((product) => product.identifiers.isSuperSku === true);
        const parentSuperSkuMaps = superSkuProducts.map((product) => ({
          parentId: product.identifiers?.parentId,
          itemId: product.identifiers.itemId
        }));
        const parentSkus = parentSuperSkuMaps.map((product) => product.parentId);

        // extra logic for non superskus
        const skuProducts = searchData
          .choicebasedProducts.filter((product) => product.identifiers.isSuperSku === false);
        const itemIdsFromSkuProducts = skuProducts.map((product) => product.identifiers.itemId);

        const productsMetadata = await getproductsMetadata(metadataBaseUrl, parentSkus);
        const appliedDimensions = transformedData[selectedChoice.id]?.dimensions;
        const filteredskusWithParent = queryChildSkusFromParent(productsMetadata, appliedDimensions);
        const filteredskus = filteredskusWithParent.map((item) => item.itemId);
        const allSkus = [...filteredskus, ...itemIdsFromSkuProducts];

        const collectionSkusData = await searchcollectionSkus(
          graphBaseUrl, skusPerBatch, storeId, zipCode, allSkus);
        selectedCategoryCollectionData.choiceBasedProducts = transformtochoicebasedProducts(
          selectedChoice.id, collectionSkusData);

        selectedCategoryCollectionData.allSkus = allSkus;

        const itemCategories = searchData.choicebasedProducts.map((product) => ({
          itemId: product.identifiers.itemId,
          categoryHierarchy: product.info?.categoryHierarchy
        }));
        itemCategories.push(...(filteredskusWithParent.map((item) => ({
          itemId: item.itemId,
          categoryHierarchy: itemCategories.find((parent) => (
            parent.itemId === parentSuperSkuMaps.find((parentProduct) => (
              parentProduct.parentId === item.parentId
            )).itemId)
          )?.categoryHierarchy || []
        }))));
        selectedCategoryCollectionData.itemCategories = itemCategories;
      } catch (error) {
        let thdErrorAnalyticsEvent = {
          category: {
            primaryCategory: 'error'
          },
          eventInfo: {
            eventName: 'post page error',
            error: [{
              type: 'system',
              displayType: 'inline',
              pageSection: 'graphql searchModel',
              message: error.message,
              code: error.code
            }]
          }
        };
        Analytics.sendAnalyticsEvent(thdErrorAnalyticsEvent);
        throw new Error('Error in getCollectionSkusAfterDataProcessed:', error);
      }
    } else {
      let thdErrorAnalyticsEvent = {
        category: {
          primaryCategory: 'error'
        },
        eventInfo: {
          eventName: 'post page error',
          error: [{
            type: 'advisory',
            displayType: 'page',
            pageSection: 'page',
            message: 'Matching Choice not found in choiceMetadata',
            code: 'Not Found'
          }]
        }
      };
      Analytics.sendAnalyticsEvent(thdErrorAnalyticsEvent);
      throw new Error('Matching Choice not found in choiceMetadata');
    }
  }
  return selectedCategoryCollectionData;
};

export const getCashedCollectionSkusAfterDataProcessed = async (
  selectedChoice, choices, choiceMetadata, host,
  metadataBaseUrl, skusPerBatch, storeId, zipCode, omsId) => {
  const selectedCategoryCollectionData = {};

  if (choices && choiceMetadata) {
    if (selectedChoice) {
      const data = choiceMetadata[selectedChoice.id];
      const searchParams = data.searchParams;
      const sections = data.sections;
      const choiceid = parseInt(selectedChoice?.id, 10);

      try {
        const cachedocument = await tryGetCabinetSellerCacheWithCollection(host, choiceid, searchParams, omsId);

        if (cachedocument) {

          const allSkusAndCategories = cachedocument.products?.map((product) => ({
            sku: product.itemID,
            categories: product.categoryHierarchy,
            keyProductFeatures: product.keyProductFeatures,
          }));

          const allSkus = allSkusAndCategories.map((item) => item.sku);
          const itemCategories = allSkusAndCategories.map((item) => ({
            itemId: item.sku,
            categoryHierarchy: item.categories,
          }));

          const itemCachedData = allSkusAndCategories.map((item) => ({
            itemId: item.sku,
            categoryHierarchy: item.categories,
            keyProductFeatures: item.keyProductFeatures,
          }));

          selectedCategoryCollectionData.itemCategories = itemCategories;
          selectedCategoryCollectionData.itemCachedData = itemCachedData;

        }

      } catch (error) {
        let thdErrorAnalyticsEvent = {
          category: {
            primaryCategory: 'error'
          },
          eventInfo: {
            eventName: 'post page error',
            error: [{
              type: 'system',
              displayType: 'inline',
              pageSection: 'blinds cacheddocumentapi',
              message: error.message,
              code: error.code
            }]
          }
        };
        Analytics.sendAnalyticsEvent(thdErrorAnalyticsEvent);
        throw new Error('Error in getCollectionSkusAfterDataProcessed:', error);
      }
    } else {
      let thdErrorAnalyticsEvent = {
        category: {
          primaryCategory: 'error'
        },
        eventInfo: {
          eventName: 'post page error',
          error: [{
            type: 'advisory',
            displayType: 'page',
            pageSection: 'page',
            message: 'Matching Choice not found in choiceMetadata',
            code: 'Not Found'
          }]
        }
      };
      Analytics.sendAnalyticsEvent(thdErrorAnalyticsEvent);
      throw new Error('Matching Choice not found in choiceMetadata');
    }
  }
  return selectedCategoryCollectionData;
};

export const saveCabinetsCategoryAndFiltersInLocalStorage = (omsId, category, filters) => {
  let data = JSON.parse(localStorage.omsId || null) || {};
  data.category = category;
  data.filters = filters;
  localStorage.setItem(omsId, JSON.stringify(data));
};

export const getPipQueryOptions = (pipTimeout) => {
  return ({ options, queryName }) => {
    if (queryName === 'product') {
      let to = 3000;
      if (pipTimeout) {
        if (typeof pipTimeout === 'string') {
          to = parseInt(pipTimeout, 10);
          // eslint-disable-next-line no-restricted-globals
          if (isNaN(to)) {
            to = 3000;
          }
        } else if (typeof pipTimeout === 'number') {
          to = pipTimeout;
        }
      }
      return {
        ...options,
        context: {
          ...(options.context || {}),
          timeout: to
        }
      };
    }
    if (queryName === 'productClientOnlyProduct') {
      return {
        ...options,
        context: {
          ...(options.context || {}),
          withAuth: true
        }
      };
    }
    return options;
  };
};

export const getPipDefaultVariables = (storeId, zipCode) => {
  return {
    filters: {
      isVerifiedPurchase: false,
      prosCons: null,
      starRatings: null
    },
    searchTerm: null,
    sortBy: 'photoreview',
    startIndex: 1,
    maxItems: 6,
    storeId,
    zipCode
  };
};
export const getSearchDefaultVariables = (storeId, zipCode) => {
  return {
    storeId,
    zipCode
  };
};

export const merge = {
  productClientOnlyProduct: {
    queries: ['product', 'clientOnlyProduct'],
    fn: ({ query, mounted }) => {

      if (!mounted) return query;

      if (query === 'product' || query === 'clientOnlyProduct') {
        return 'product';
      }
      return query;
    }
  }
};

export const advancedFiltersToMap = (advancedFilters) => {
  let result = {};
  (advancedFilters || []).forEach((filter) => {
    let keyValue = filter.split('-');
    const filterName = keyValue[0]?.trim();
    let filterGroup = result[filterName];
    if (!filterGroup) {
      filterGroup = [];
      result[filterName] = filterGroup;

    }
    filterGroup.push(keyValue[1]?.trim());
  });

  return result;
};

export const advancedFiltersToKeyValueArr = (advancedFilters) => {
  const mappedResults = advancedFiltersToMap(advancedFilters);
  const keys = Object.keys(mappedResults);

  return keys.map((key) => ({ name: key, values: mappedResults[key] }));
};

const getItemFromLocalStorage = (omsId) => {
  const defaultValue = null;
  if (typeof window === 'undefined') return defaultValue;
  if (!window.localStorage) return defaultValue;

  return window.localStorage.getItem(omsId);
};

export const getCategoryFromLocalStorage = (omsId) => {
  const item = getItemFromLocalStorage(omsId);

  if (item) return JSON.parse(item).category;

  return '';
};

export const getFiltersFromLocalStorage = (omsId) => {
  const item = getItemFromLocalStorage(omsId);

  if (item) return JSON.parse(item).filters;

  return '';
};

const getBrandNameFilters = (collections) => {
  const brandMap = collections?.products?.reduce((acc, product) => {
    const brand = product.brandName || 'N/A';
    const itemId = product.itemId;
    if (!acc[brand]) {
      acc[brand] = { itemIds: [], count: 0 };
    }
    acc[brand].itemIds.push(itemId);
    acc[brand].count += 1;
    return acc;
  }, {});

  const distinctBrandNames = Object.keys(brandMap);

  const brandFilters = { label: 'Brand Name', refinements: [] };
  brandFilters.refinements = distinctBrandNames.map((brand) => ({
    label: brand,
    recordCount: brandMap[brand].count,
    itemIds: brandMap[brand].itemIds
  }));

  return brandFilters;
};

const getColorFinishFilters = (collections) => {
  const colorMap = collections?.products?.reduce((acc, product) => {
    const color = product.colorFinishFamily || 'N/A';
    const itemId = product.itemId;
    if (!acc[color]) {
      acc[color] = { itemIds: [], count: 0 };
    }
    acc[color].itemIds.push(itemId);
    acc[color].count += 1;
    return acc;
  }, {});

  const distinctColorNames = Object.keys(colorMap);

  const colorFilters = { label: 'Color Finish Family', refinements: [] };
  colorFilters.refinements = distinctColorNames.map((color) => ({
    label: color,
    recordCount: colorMap[color].count,
    itemIds: colorMap[color].itemIds
  }));

  return colorFilters;
};
const getFiltersForCollectionDrawer = (collections) => {
  if (!collections || !collections.primaryFilters) return [];
  const doorStyleFilters = collections.primaryFilters[0];
  doorStyleFilters.label = 'Door Style';
  const filters = [doorStyleFilters, getBrandNameFilters(collections), getColorFinishFilters(collections)];

  return filters;
};
export const getFilteredCollectionDrawerProducts = (selectedFilters, collections) => {
  if (!selectedFilters || selectedFilters.length === 0 || !Array.isArray(selectedFilters)) {
    return collections?.products || [];
  }
  const filters = getFiltersForCollectionDrawer(collections);
  // Group selected values by filter name
  const filterGroups = selectedFilters.reduce((acc, filter) => {
    const [filterName, filterValue] = filter.split(' - ');
    if (!acc[filterName]) acc[filterName] = [];
    acc[filterName].push(filterValue);
    return acc;
  }, {});

  const getItemIdsForFilter = (filterLabel, selectedValues) => {
    const filter = filters.find((fl) => fl.label === filterLabel);
    if (!filter) return [];
    return filter.refinements
      .filter((refinement) => selectedValues.includes(refinement.label))
      .flatMap((refinement) => refinement.itemIds);
  };

  // Get itemIds for each filter type
  const brandItemIds = filterGroups['Brand Name']
    ? getItemIdsForFilter('Brand Name', filterGroups['Brand Name']) : null;
  const colorItemIds = filterGroups['Color Finish Family']
    ? getItemIdsForFilter('Color Finish Family', filterGroups['Color Finish Family']) : null;
  const doorStyleItemIds = filterGroups['Door Style']
    ? getItemIdsForFilter('Door Style', filterGroups['Door Style']) : null;

  // Filter dataSource by intersection of all active filter itemIds
  let filteredData = collections?.products || [];
  const activeFilters = [brandItemIds, colorItemIds, doorStyleItemIds].filter(Boolean);

  if (activeFilters.length > 0) {
    const commonItemIds = activeFilters.reduce((acc, ids) => acc.filter((id) => ids.includes(id))
    );
    filteredData = collections?.products.filter((item) => commonItemIds.includes(item.itemId));
  }
  return filteredData;
};
